import { useState, useEffect, useCallback } from 'react';
import type { ReactNode } from 'react';
import { FileText, Plus, Pencil, Trash2, X, BookOpenCheck } from 'lucide-react';
import { Link } from 'react-router-dom';
import PageHeader from '../../../components/PageHeader';
import WhsGate from '../../../components/whs/WhsGate';
import Button from '../../../components/ui/Button';
import whsService from '../../../services/whs';
import inductionService from '../../../services/induction';
import { PolicyMarkdown } from '../../inductions/ReadPolicy';
import userService from '../../../services/user';
import type { UserData } from '../../../services/user';
import type { WhsDocument, DocumentType, DocumentStatus } from '../../../types/whs';
import { Badge, DOC_TYPE_LABEL, DOC_STATUS_LABEL, DOC_STATUS_CLASS } from '../../../components/whs/whsConstants';
import { useOrgTimezone, formatLocalDateInTz, getTodayInTz } from '../../../utils/timezone';

const inputCls = 'w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500';
const labelCls = 'block text-xs font-medium text-neutral-600 mb-1';

const orNull = (v: string) => (v.trim() === '' ? null : v.trim());
const userLabel = (u: UserData) => [u.first_name, u.last_name].filter(Boolean).join(' ') || u.username;

function Modal({ title, onClose, children, footer }: { title: string; onClose: () => void; children: ReactNode; footer?: ReactNode }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      {/* Wide, and the body scrolls between a fixed header and footer: a policy
          runs to pages, and Save must not scroll away beneath it. */}
      <div className="flex w-full max-w-4xl max-h-[92vh] flex-col rounded-xl bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex shrink-0 items-center justify-between border-b border-neutral-200 px-5 py-3">
          <h3 className="text-base font-semibold text-neutral-900">{title}</h3>
          <button onClick={onClose} className="text-neutral-400 hover:text-neutral-600" aria-label="Close"><X className="h-5 w-5" /></button>
        </div>
        <div className="min-h-0 overflow-y-auto px-5 py-4">{children}</div>
        {footer && <div className="flex shrink-0 justify-end gap-2 border-t border-neutral-200 px-5 py-3">{footer}</div>}
      </div>
    </div>
  );
}

interface DocForm {
  title: string;
  doc_type: DocumentType;
  reference_code: string;
  version: string;
  description: string;
  content: string;
  status: DocumentStatus;
  owner_id: string;
  effective_date: string;
  review_date: string;
  requires_acknowledgement: boolean;
  audience: 'everyone' | 'employees' | 'casuals' | 'contractors';
  pack_order: string;
  material_change: boolean;
}

const emptyForm: DocForm = {
  title: '', doc_type: 'procedure', reference_code: '', version: '', description: '',
  content: '', status: 'draft', owner_id: '', effective_date: '', review_date: '',
  requires_acknowledgement: false, audience: 'everyone', pack_order: '100', material_change: false,
};

const AUDIENCE_LABEL: Record<DocForm['audience'], string> = {
  everyone: 'Everyone', employees: 'Employees', casuals: 'Casuals', contractors: 'Contractors',
};

interface PanelProps {
  /** Which page owns the documents: the WHS register, or Company Policies. */
  section?: 'whs' | 'employment';
  /** Inside another page: no page header, no filters. */
  embedded?: boolean;
  /** Bump to reload, e.g. after starter policies are installed. */
  reloadKey?: number;
}

function DocumentsInner({ section = 'whs', embedded = false, reloadKey = 0 }: PanelProps) {
  const orgTz = useOrgTimezone();
  // An employment policy is one every employee acknowledges, by default.
  const blankForm: DocForm = section === 'employment'
    ? { ...emptyForm, doc_type: 'policy', requires_acknowledgement: true, audience: 'employees' }
    : emptyForm;
  const today = getTodayInTz(orgTz);

  const [docs, setDocs] = useState<WhsDocument[]>([]);
  const [users, setUsers] = useState<UserData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [typeFilter, setTypeFilter] = useState<DocumentType | ''>('');
  const [statusFilter, setStatusFilter] = useState<DocumentStatus | ''>('');

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<WhsDocument | null>(null);
  const [form, setForm] = useState<DocForm>(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await whsService.listDocuments({
      doc_type: typeFilter || undefined,
      status: statusFilter || undefined,
      section,
    });
    if (res.success) { setDocs(res.data); setError(null); } else setError(res.error.message);
    setLoading(false);
    // reloadKey is only a trigger: bumping it re-runs the load.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeFilter, statusFilter, section, reloadKey]);

  const loadUsers = useCallback(async () => {
    const res = await userService.getUsers();
    if (res.success) setUsers(res.data);
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadUsers(); }, [loadUsers]);

  // The draft with its {{markers}} filled from the current settings; null while editing.
  const [preview, setPreview] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const togglePreview = async () => {
    if (preview !== null) { setPreview(null); return; }
    setPreviewLoading(true);
    try {
      setPreview((await inductionService.previewPolicy(form.content)).content);
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : 'Could not preview the policy.');
    } finally {
      setPreviewLoading(false);
    }
  };

  const openCreate = () => { setEditing(null); setForm(blankForm); setPreview(null); setModalOpen(true); };
  const openEdit = (d: WhsDocument) => {
    setEditing(d);
    setForm({
      title: d.title,
      doc_type: d.doc_type,
      reference_code: d.reference_code ?? '',
      version: d.version ?? '',
      description: d.description ?? '',
      content: d.content ?? '',
      status: d.status,
      owner_id: d.owner_id != null ? String(d.owner_id) : '',
      effective_date: d.effective_date ?? '',
      review_date: d.review_date ?? '',
      requires_acknowledgement: !!d.requires_acknowledgement,
      audience: d.audience ?? 'everyone',
      pack_order: String(d.pack_order ?? 100),
      material_change: false,
    });
    setPreview(null);
    setModalOpen(true);
  };

  const submit = async () => {
    if (!form.title.trim()) { setError('Title is required.'); return; }
    setSaving(true);
    const payload: Partial<WhsDocument> = {
      title: form.title.trim(),
      doc_type: form.doc_type,
      reference_code: orNull(form.reference_code),
      version: orNull(form.version),
      description: orNull(form.description),
      content: orNull(form.content),
      status: form.status,
      owner_id: form.owner_id ? Number(form.owner_id) : null,
      effective_date: orNull(form.effective_date),
      review_date: orNull(form.review_date),
      requires_acknowledgement: form.requires_acknowledgement,
      audience: form.audience,
      pack_order: Number.isFinite(Number(form.pack_order)) ? Number(form.pack_order) : 100,
      ...(form.material_change ? { material_change: true } : {}),
      ...(editing ? {} : { section }),
    };
    const res = editing
      ? await whsService.updateDocument(editing.id, payload)
      : await whsService.createDocument(payload);
    setSaving(false);
    if (res.success) { setModalOpen(false); setError(null); await load(); }
    else setError(res.error.message);
  };

  const remove = async (d: WhsDocument) => {
    if (!window.confirm(`Delete document "${d.title}"?`)) return;
    const res = await whsService.deleteDocument(d.id);
    if (res.success) await load(); else setError(res.error.message);
  };

  const body = (
    <>
        {error && (
          <div className="mb-6 rounded-lg bg-red-50 border border-red-200 p-4 text-sm text-red-700">{error}</div>
        )}

        <div className="mb-6 flex flex-wrap items-end gap-3">
          {!embedded && (<>
          <div>
            <label className={labelCls}>Document type</label>
            <select className={inputCls} value={typeFilter} onChange={(e) => setTypeFilter(e.target.value as DocumentType | '')}>
              <option value="">All types</option>
              {(Object.entries(DOC_TYPE_LABEL) as [DocumentType, string][]).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelCls}>Status</label>
            <select className={inputCls} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as DocumentStatus | '')}>
              <option value="">All statuses</option>
              {(Object.entries(DOC_STATUS_LABEL) as [DocumentStatus, string][]).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
          </div>
          </>)}
          <div className="ml-auto flex flex-wrap gap-2">
            <Link to="/inductions?tab=policies">
              <Button variant="secondary"><BookOpenCheck className="h-4 w-4" /> Induction pack</Button>
            </Link>
            <Button onClick={openCreate}><Plus className="h-4 w-4" /> {section === 'employment' ? 'New policy' : 'New document'}</Button>
          </div>
        </div>

        <div className="overflow-x-auto rounded-xl border border-neutral-200 bg-white">
          <table className="min-w-full divide-y divide-neutral-200 text-sm">
            <thead className="bg-neutral-50 text-left text-xs font-semibold uppercase tracking-wide text-neutral-500">
              <tr>
                <th className="px-4 py-3">Title</th>
                <th className="px-4 py-3">Type</th>
                <th className="px-4 py-3">Ref</th>
                <th className="px-4 py-3">Ver.</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Owner</th>
                <th className="px-4 py-3">Effective</th>
                <th className="px-4 py-3">Review</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {loading ? (
                <tr><td colSpan={9} className="px-4 py-8 text-center text-neutral-400">Loading…</td></tr>
              ) : docs.length === 0 ? (
                <tr><td colSpan={9} className="px-4 py-8 text-center text-neutral-400">{section === 'employment' ? 'No policy documents yet.' : 'No documents found.'}</td></tr>
              ) : docs.map((d) => {
                const reviewPast = !!d.review_date && d.review_date < today;
                return (
                  <tr key={d.id} className="hover:bg-neutral-50">
                    <td className="px-4 py-3 font-medium text-neutral-900">
                      {d.title}
                      {d.requires_acknowledgement && (
                        <Badge label="Induction pack" className="ml-2 bg-lime-100 text-lime-800" />
                      )}
                    </td>
                    <td className="px-4 py-3"><Badge label={DOC_TYPE_LABEL[d.doc_type]} className="bg-slate-100 text-slate-700" /></td>
                    <td className="px-4 py-3 text-neutral-600">{d.reference_code || '—'}</td>
                    <td className="px-4 py-3 text-neutral-600">{d.version || '—'}</td>
                    <td className="px-4 py-3"><Badge label={DOC_STATUS_LABEL[d.status]} className={DOC_STATUS_CLASS[d.status]} /></td>
                    <td className="px-4 py-3 text-neutral-600">{[d.owner_first_name, d.owner_last_name].filter(Boolean).join(' ') || '—'}</td>
                    <td className="px-4 py-3 text-neutral-600">{d.effective_date ? formatLocalDateInTz(d.effective_date, orgTz) : '—'}</td>
                    <td className={`px-4 py-3 ${reviewPast ? 'font-semibold text-red-600' : 'text-neutral-600'}`}>
                      {d.review_date ? formatLocalDateInTz(d.review_date, orgTz) : '—'}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-1">
                        <button onClick={() => openEdit(d)} className="rounded p-1.5 text-neutral-500 hover:bg-neutral-100 hover:text-neutral-800" aria-label="Edit"><Pencil className="h-4 w-4" /></button>
                        <button onClick={() => remove(d)} className="rounded p-1.5 text-neutral-500 hover:bg-red-50 hover:text-red-600" aria-label="Delete"><Trash2 className="h-4 w-4" /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

      {modalOpen && (
        <Modal
          title={editing ? 'Edit document' : 'New document'}
          onClose={() => setModalOpen(false)}
          footer={(
            <>
              <Button variant="secondary" onClick={() => setModalOpen(false)}>Cancel</Button>
              <Button onClick={submit} isLoading={saving}>{editing ? 'Save changes' : 'Create'}</Button>
            </>
          )}
        >
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <label className={labelCls}>Title *</label>
              <input className={inputCls} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
            </div>
            <div>
              <label className={labelCls}>Document type</label>
              <select className={inputCls} value={form.doc_type} onChange={(e) => setForm({ ...form, doc_type: e.target.value as DocumentType })}>
                {(Object.entries(DOC_TYPE_LABEL) as [DocumentType, string][]).map(([k, v]) => (
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelCls}>Status</label>
              <select className={inputCls} value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value as DocumentStatus })}>
                {(Object.entries(DOC_STATUS_LABEL) as [DocumentStatus, string][]).map(([k, v]) => (
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelCls}>Reference code</label>
              <input className={inputCls} value={form.reference_code} onChange={(e) => setForm({ ...form, reference_code: e.target.value })} />
            </div>
            <div>
              <label className={labelCls}>Version</label>
              <input className={inputCls} value={form.version} onChange={(e) => setForm({ ...form, version: e.target.value })} />
            </div>
            <div>
              <label className={labelCls}>Owner</label>
              <select className={inputCls} value={form.owner_id} onChange={(e) => setForm({ ...form, owner_id: e.target.value })}>
                <option value="">— Unassigned —</option>
                {users.map((u) => <option key={u.id} value={u.id}>{userLabel(u)}</option>)}
              </select>
            </div>
            <div />
            <div>
              <label className={labelCls}>Effective date</label>
              <input type="date" className={inputCls} value={form.effective_date} onChange={(e) => setForm({ ...form, effective_date: e.target.value })} />
            </div>
            <div>
              <label className={labelCls}>Review date</label>
              <input type="date" className={inputCls} value={form.review_date} onChange={(e) => setForm({ ...form, review_date: e.target.value })} />
            </div>
            <div className="sm:col-span-2">
              <label className={labelCls}>Description</label>
              <textarea rows={2} className={inputCls} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>
            <div className="sm:col-span-2">
              <div className="mb-1 flex flex-wrap items-end justify-between gap-2">
                <label htmlFor="whs-doc-content" className={labelCls}>Content</label>
                <button
                  type="button"
                  onClick={togglePreview}
                  className="text-xs font-medium text-primary-700 hover:underline"
                >
                  {preview !== null ? 'Back to editing' : previewLoading ? 'Loading…' : 'Preview as staff see it'}
                </button>
              </div>
              {preview !== null ? (
                <div className="min-h-[16rem] h-[55vh] overflow-y-auto rounded-lg border border-neutral-300 bg-white px-4 py-3">
                  <PolicyMarkdown text={preview} />
                </div>
              ) : (
                <textarea
                  id="whs-doc-content"
                  rows={20}
                  className={`${inputCls} min-h-[16rem] h-[55vh] resize-y leading-relaxed`}
                  value={form.content}
                  onChange={(e) => setForm({ ...form, content: e.target.value })}
                />
              )}
              <p className="mt-1 text-xs text-neutral-500">
                <code>## Heading</code> · <code>- bullet</code> · <code>1. numbered</code> · <code>**bold**</code> · <code>*italic*</code>. A blank line starts a new paragraph.
              </p>
              <p className="mt-1 text-xs text-neutral-500 break-words">
                Markers are filled from your settings whenever the policy is read, so the figures stay current:{' '}
                <code>{'{{org_name}}'}</code> <code>{'{{work_schedule}}'}</code> <code>{'{{ordinary_hours}}'}</code>{' '}
                <code>{'{{lunch_minutes}}'}</code> <code>{'{{lunch_threshold}}'}</code> <code>{'{{annual_leave}}'}</code>{' '}
                <code>{'{{personal_leave}}'}</code> <code>{'{{sick_cert_days}}'}</code>. When a settings change alters a
                published policy's words, staff are asked to acknowledge it again.
              </p>
            </div>
            <div className="sm:col-span-2 rounded-lg border border-lime-200 bg-lime-50 p-3 space-y-3">
              <label className="flex items-start gap-2 text-sm text-neutral-800">
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={form.requires_acknowledgement}
                  onChange={(e) => setForm({ ...form, requires_acknowledgement: e.target.checked })}
                />
                <span>
                  <span className="font-medium">Part of the induction pack.</span> Everyone in the audience must read it and
                  acknowledge it in the app; the date and time are recorded. It goes out once the status is Current.
                </span>
              </label>
              {form.requires_acknowledgement && (
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div>
                    <label className={labelCls}>Who must acknowledge it</label>
                    <select className={inputCls} value={form.audience} onChange={(e) => setForm({ ...form, audience: e.target.value as DocForm['audience'] })}>
                      {(Object.entries(AUDIENCE_LABEL) as [DocForm['audience'], string][]).map(([k, v]) => (
                        <option key={k} value={k}>{v}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className={labelCls}>Order in the pack</label>
                    <input type="number" className={inputCls} value={form.pack_order} onChange={(e) => setForm({ ...form, pack_order: e.target.value })} />
                  </div>
                  {editing && (editing.ack_version ?? 0) > 0 && (
                    <label className="sm:col-span-2 flex items-start gap-2 text-sm text-neutral-800">
                      <input
                        type="checkbox"
                        className="mt-0.5"
                        checked={form.material_change}
                        onChange={(e) => setForm({ ...form, material_change: e.target.checked })}
                      />
                      <span>
                        This changes what the policy means — ask everyone to read and acknowledge it again.
                        Leave unticked for a typo or layout fix.
                      </span>
                    </label>
                  )}
                </div>
              )}
            </div>
          </div>
        </Modal>
      )}
    </>
  );

  if (embedded) return body;
  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title="Safety Documents"
        subtitle="Controlled documents register — policies, procedures, SWMS, SOPs, forms & SDS"
        icon={<FileText className="h-6 w-6 text-white" />}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">{body}</div>
    </div>
  );
}

/** Policy documents owned by another page (Company Policies). The caller gates on the HR seat. */
export function PolicyDocumentsPanel(props: Omit<PanelProps, 'embedded'>) {
  return <DocumentsInner {...props} embedded />;
}

export default function WhsDocuments() {
  return <WhsGate><DocumentsInner /></WhsGate>;
}
